/*
 * Roas Digitall — room visualizer launcher for the Borr Halı theme.
 *
 * The block (blocks/borr-try-in-room.liquid) prints <borr-room-launch data-src="…">. Its button opens the
 * visualizer app in a layer over the product page; the app runs in an iframe and talks to this page with
 * postMessage, because only this page can touch the shop's cart.
 *
 * Messages from the app: ready, close, toggle-fullscreen, add-to-cart, get-cart, cart-remove, open-cart,
 * checkout, view-cart, navigate, call, edge. Replies to the app: cart-result, cart.
 */
(function () {
  'use strict';
  if (window.BorrRoomLaunch) return;

  var CHANNEL = 'borr-visualizer';
  var VEIL_MAX_MS = 20000;
  var FADE_MS = 300;

  var ICONS = {
    close: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
    grow: '<path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>',
    shrink: '<path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/><path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>',
  };
  function icon(name) {
    return '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + ICONS[name] + '</svg>';
  }

  function track(event, extra) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, extra));
    } catch (_) { /* analytics must never break the page */ }
  }

  /* ------------------------------------------------------------------ health */

  // Anonymous counts for the visualizer's own dashboard: was the button pressed, did the app open and how fast, and
  // on which kind of browser, so a phone it fails on shows up. Random ids only; a coarse browser label, never the
  // user agent itself.
  var HEALTH_URL = 'https://borr-api.ayhan-b98.workers.dev/event';

  function randomId() { return (Math.random().toString(36).slice(2, 10) + Date.now().toString(36)).replace(/[^a-z0-9]/g, ''); }
  function keptId(kind, key) {
    try {
      var store = kind === 'local' ? window.localStorage : window.sessionStorage;
      var v = store.getItem(key);
      if (!v) { v = randomId(); store.setItem(key, v); }
      return v;
    } catch (_) { return randomId(); }
  }
  function browserTag() {
    var ua = navigator.userAgent;
    var ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    var os = ios ? 'ios' + ((ua.match(/OS (\d+)_/) || [])[1] || '') : /Android/.test(ua) ? 'android' + ((ua.match(/Android (\d+)/) || [])[1] || '')
      : /Windows/.test(ua) ? 'windows' : /Mac OS X/.test(ua) ? 'mac' : 'other';
    var app = /Instagram/.test(ua) ? 'instagram' : /FBAN|FBAV|FB_IAB/.test(ua) ? 'facebook' : /musical_ly|TikTok|Bytedance/i.test(ua) ? 'tiktok'
      : /SamsungBrowser/.test(ua) ? 'samsung' : /EdgA?\//.test(ua) ? 'edge' : /CriOS|Chrome\//.test(ua) ? 'chrome' : /FxiOS|Firefox\//.test(ua) ? 'firefox'
      : /Safari\//.test(ua) ? 'safari' : 'webview';
    return app + '-' + os;
  }
  function health(name, rug, props) {
    try {
      var body = JSON.stringify({ events: [{
        v: keptId('local', 'borr-hv'), s: keptId('session', 'borr-hs'), n: name, rug: rug || undefined,
        dev: window.matchMedia('(max-width: 759px)').matches ? 'phone' : 'desktop', emb: 1,
        p: Object.assign({ b: browserTag() }, props),
      }] });
      var sent = navigator.sendBeacon && navigator.sendBeacon(HEALTH_URL, new Blob([body], { type: 'text/plain' }));
      if (!sent) fetch(HEALTH_URL, { method: 'POST', body: body, headers: { 'content-type': 'text/plain' }, keepalive: true }).catch(function () {});
    } catch (_) { /* counting must never break the page */ }
  }

  /* ------------------------------------------------------------------ page scroll */

  var pageScroll = {
    y: 0,
    // The page only stops scrolling; it is not pinned with position:fixed any more. Pinning it while an iPhone
    // browser's toolbar had shrunk left a white strip under the window (the browser's own background, which no page
    // colour reaches). The window covers the page, so a page that could still move under it would not be seen.
    hold: function () {
      this.y = window.scrollY || 0;
      document.documentElement.classList.add('brl-noscroll');
    },
    release: function () {
      document.documentElement.classList.remove('brl-noscroll');
    },
    restore: function () { window.scrollTo(0, this.y); },
  };

  /* ------------------------------------------------------------------ fullscreen */

  function fullscreenElement() { return document.fullscreenElement || document.webkitFullscreenElement || null; }
  function canFullscreen(el) { return !!(el.requestFullscreen || el.webkitRequestFullscreen); }
  function exitFullscreen() {
    var fn = document.exitFullscreen || document.webkitExitFullscreen;
    if (fn && fullscreenElement()) { try { fn.call(document); } catch (_) { /* already out */ } }
  }

  /* ------------------------------------------------------------------ the layer */

  function Layer() {
    this.el = null;
    this.box = null;
    this.frame = null;
    this.cover = null;
    this.sizeBtn = null;
    this.trigger = null;
    this.origin = '';
    this.labels = {};
    this.isOpen = false;
    this.ownsHistory = false;
    this.coverTimer = 0;
    this.handle = '';
    this.openedAt = 0;
    this.readyAt = 0;
  }

  Layer.prototype.create = function () {
    var el = document.createElement('div');
    el.className = 'brl';
    el.hidden = true;
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.innerHTML =
      '<div class="brl__box">' +
        '<div class="brl__bar">' +
          '<button type="button" class="brl__btn brl__size"></button>' +
          '<button type="button" class="brl__btn brl__shut">' + icon('close') + '<span></span></button>' +
        '</div>' +
        '<div class="brl__view">' +
          '<div class="brl__cover"><i class="brl__ring"></i><span></span></div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(el);

    this.el = el;
    this.box = el.querySelector('.brl__box');
    this.cover = el.querySelector('.brl__cover');
    this.sizeBtn = el.querySelector('.brl__size');
    // a phone browser that cannot put an element in fullscreen gets no button rather than one that does nothing
    this.sizeBtn.hidden = !canFullscreen(this.box);

    var self = this;
    el.addEventListener('click', function (e) { if (e.target === el) self.hide(); });
    el.querySelector('.brl__shut').addEventListener('click', function () { self.hide(); });
    this.sizeBtn.addEventListener('click', function () { self.toggleSize(); });
    var sync = function () { self.paintSizeButton(); };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
  };

  Layer.prototype.isBig = function () {
    return !!fullscreenElement() || this.el.classList.contains('brl--max');
  };

  Layer.prototype.paintSizeButton = function () {
    if (!this.sizeBtn) return;
    var big = this.isBig();
    this.sizeBtn.innerHTML = icon(big ? 'shrink' : 'grow') + '<span></span>';
    this.sizeBtn.querySelector('span').textContent = big ? this.labels.fullExit : this.labels.full;
    this.sizeBtn.setAttribute('aria-pressed', String(big));
  };

  Layer.prototype.toggleSize = function () {
    if (fullscreenElement()) { exitFullscreen(); return; }
    var self = this;
    var request = this.box.requestFullscreen || this.box.webkitRequestFullscreen;
    var fallback = function () { self.el.classList.toggle('brl--max'); self.paintSizeButton(); };
    if (!request) { fallback(); return; }
    var p = request.call(this.box);
    if (p && typeof p.catch === 'function') p.catch(fallback);
  };

  Layer.prototype.show = function (host) {
    if (this.isOpen) return;
    var src = host.getAttribute('data-src') || '';
    var url;
    try { url = new URL(src); } catch (_) { return; }
    if (url.protocol !== 'https:' && url.hostname !== 'localhost') return;
    if (!this.el) this.create();

    var d = host.dataset;
    this.labels = {
      close: d.labelClose || 'Kapat',
      wait: d.labelWait || 'Yükleniyor',
      full: d.labelFull || 'Tam ekran',
      fullExit: d.labelFullExit || 'Küçült',
    };
    this.origin = url.origin;
    this.trigger = host.querySelector('button') || host;
    this.el.setAttribute('aria-label', d.name || 'Room visualizer');
    this.el.querySelector('.brl__shut span').textContent = this.labels.close;
    this.cover.querySelector('span').textContent = this.labels.wait;
    this.cover.classList.remove('brl__cover--gone');
    this.paintSizeButton();

    var frame = document.createElement('iframe');
    frame.className = 'brl__frame';
    frame.title = d.name || 'Room visualizer';
    // sharing and copying from inside a cross-origin frame only work when this page grants them
    frame.allow = 'fullscreen; web-share; clipboard-write; accelerometer; gyroscope; xr-spatial-tracking';
    frame.setAttribute('allowfullscreen', ''); // Safari still reads the old attribute
    frame.src = url.href;
    this.el.querySelector('.brl__view').insertBefore(frame, this.cover);
    this.frame = frame;

    this.el.hidden = false;
    pageScroll.hold();
    var el = this.el;
    requestAnimationFrame(function () { el.classList.add('brl--on'); });
    this.isOpen = true;

    this.handle = d.handle || '';
    this.openedAt = Date.now();
    this.readyAt = 0;
    health('launch', this.handle);

    var cover = this.cover, me = this;
    clearTimeout(this.coverTimer);
    this.coverTimer = setTimeout(function () {
      cover.classList.add('brl__cover--gone');
      if (me.isOpen && !me.readyAt) health('launch_timeout', me.handle, { ms: VEIL_MAX_MS });
    }, VEIL_MAX_MS);

    // the phone's Back gesture closes the layer instead of leaving the product page
    try { history.pushState({ brl: 1 }, ''); this.ownsHistory = true; } catch (_) { this.ownsHistory = false; }
    var shut = this.el.querySelector('.brl__shut');
    requestAnimationFrame(function () { shut.focus(); });
    track('borr_visualizer_open', { borr_product: d.handle || '' });
  };

  Layer.prototype.hide = function (opts) {
    if (!this.isOpen) return;
    var leavingHistory = opts && opts.historyHandled;
    this.isOpen = false;
    if (!this.readyAt) health('launch_close_early', this.handle, { ms: Date.now() - this.openedAt });
    clearTimeout(this.coverTimer);
    exitFullscreen();
    this.el.classList.remove('brl--on', 'brl--max', 'brl--aside');
    pageScroll.release();
    cart.wakeDrawer();

    if (this.ownsHistory && !leavingHistory) {
      // going back restores that history entry's scroll position, which is not where the visitor was
      addEventListener('popstate', function () { requestAnimationFrame(function () { pageScroll.restore(); }); }, { once: true });
      setTimeout(function () { pageScroll.restore(); }, 250);
      try { history.back(); } catch (_) { pageScroll.restore(); }
    } else {
      pageScroll.restore();
    }
    this.ownsHistory = false;

    var frame = this.frame, el = this.el, self = this;
    this.frame = null;
    setTimeout(function () {
      if (frame) frame.remove(); // a WebGL scene left in a hidden frame keeps the phone's GPU busy
      if (!self.isOpen) el.hidden = true;
    }, FADE_MS);
    if (this.trigger && this.trigger.focus) this.trigger.focus();
  };

  Layer.prototype.send = function (type, payload) {
    if (!this.frame || !this.frame.contentWindow || !this.origin) return;
    this.frame.contentWindow.postMessage(Object.assign({ source: CHANNEL, type: type }, payload), this.origin);
  };

  Layer.prototype.ready = function () {
    if (this.isOpen && !this.readyAt) {
      this.readyAt = Date.now();
      health('launch_ready', this.handle, { ms: this.readyAt - this.openedAt });
    }
    clearTimeout(this.coverTimer);
    if (this.cover) this.cover.classList.add('brl__cover--gone');
  };

  // the theme's cart drawer opens in the page underneath; the layer steps aside until the drawer is closed
  Layer.prototype.stepAside = function (drawer) {
    if (!this.el) return;
    exitFullscreen();
    var el = this.el;
    el.classList.add('brl--aside');
    var comeBack = function (ev) {
      if (ev.target !== drawer && !drawer.contains(ev.target)) return;
      document.removeEventListener('theme-drawer:close', comeBack);
      el.classList.remove('brl--aside');
    };
    document.addEventListener('theme-drawer:close', comeBack);
  };

  var layer = new Layer();

  /* ------------------------------------------------------------------ the shop's cart */

  var cart = {
    root: function () { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; },
    routes: function () { return (window.Theme && Theme.routes) || {}; },

    read: function () {
      return fetch(this.root() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); });
    },

    // only what the app's cart panel draws; prices stay in minor units with their currency
    brief: function (c) {
      var withWidth = function (u) { return u ? u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=200' : ''; };
      return {
        count: c.item_count || 0,
        total: c.total_price || 0,
        currency: c.currency || '',
        items: (c.items || []).map(function (line) {
          var img = line.image || (line.featured_image && line.featured_image.url);
          return {
            key: line.key,
            variantId: line.variant_id,
            handle: line.handle,
            title: line.product_title || line.title,
            variant: line.variant_title && !/default/i.test(line.variant_title) ? line.variant_title : '',
            quantity: line.quantity,
            price: line.final_line_price,
            image: withWidth(img),
          };
        }),
      };
    },

    sectionIds: function () {
      var ids = [];
      document.querySelectorAll('cart-items-component').forEach(function (n) {
        var id = n.dataset && n.dataset.sectionId;
        if (id && ids.indexOf(id) < 0) ids.push(id);
      });
      return ids;
    },

    // the theme listens for this event to update the header count and the drawer, the same as for its own forms
    announce: function (detail) {
      return import('@shopify/events').then(function (mod) {
        var Ev = mod && mod.CartLinesUpdateEvent;
        if (!Ev || !Ev.createPromise) return null;
        var pending = Ev.createPromise();
        (layer.trigger || document.body).dispatchEvent(new Ev(Object.assign({}, detail, { promise: pending.promise })));
        return { pending: pending, Ev: Ev };
      }).catch(function () { return null; });
    },

    // The theme opens its own cart drawer after an add when "auto-open" is set. While the window is open the app shows
    // its own cart instead, and on a phone the drawer (a modal dialog) would even cover the window. So the drawer is
    // kept shut while the window is open and gets its auto-open back when the window closes.
    quietDrawer: function () {
      document.querySelectorAll('cart-drawer-component[auto-open]').forEach(function (n) {
        n.removeAttribute('auto-open');
        n.setAttribute('data-brl-auto-open', '');
      });
    },
    wakeDrawer: function () {
      document.querySelectorAll('cart-drawer-component[data-brl-auto-open]').forEach(function (n) {
        n.removeAttribute('data-brl-auto-open');
        n.setAttribute('auto-open', '');
      });
    },

    // the drawer was rendered with the page; anything added since is missing until it is fetched again
    redrawDrawer: function () {
      var current = document.querySelector('cart-drawer-component');
      if (!current) return Promise.resolve();
      var wrap = current.closest('[id^="shopify-section-"]');
      var id = (current.dataset && current.dataset.sectionId) || (wrap && wrap.id.replace('shopify-section-', '')) || 'cart-drawer-section';
      return fetch('/?sections=' + encodeURIComponent(id), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          if (!json[id]) return;
          var next = new DOMParser().parseFromString(json[id], 'text/html').querySelector('cart-drawer-component');
          if (next) current.replaceWith(next);
        })
        .catch(function () { /* keep the drawer as it is */ });
    },

    add: function (msg) {
      var self = this;
      var variant = Number(msg.variantId);
      var qty = Math.min(10, Math.max(1, parseInt(msg.quantity, 10) || 1));
      if (!variant) { layer.send('cart-result', { ok: false, message: 'variant' }); return; }

      this.quietDrawer();
      var ticket = this.announce({ action: 'add', context: 'product', lines: [{ merchandiseId: String(variant), quantity: qty }] });
      var form = new FormData();
      form.set('id', String(variant));
      form.set('quantity', String(qty));
      var sections = this.sectionIds();
      if (sections.length) form.set('sections', sections.join(','));

      fetch(this.routes().cart_add_url || '/cart/add.js', {
        method: 'POST', body: form, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
        .then(function (res) {
          if (!res.ok || res.body.status) throw new Error(res.body.description || res.body.message || 'add failed');
          return self.read().then(function (c) {
            ticket.then(function (t) {
              if (t) t.pending.resolve({ cart: t.Ev.createCartFromAjaxResponse(c), detail: { items: c.items, itemCount: qty, sections: res.body.sections, source: CHANNEL, sourceId: CHANNEL, productId: msg.productId, didError: false } });
            });
            layer.send('cart-result', { ok: true, count: c.item_count, cart: self.brief(c) });
            track('borr_visualizer_add_to_cart', { borr_product: msg.handle || '', borr_variant: variant });
          });
        })
        .catch(function (err) {
          return self.read().then(function (c) {
            ticket.then(function (t) {
              if (t) t.pending.resolve({ cart: t.Ev.createCartFromAjaxResponse(c), detail: { items: c.items, itemCount: qty, source: CHANNEL, sourceId: CHANNEL, didError: true } });
            });
            // a one-off rug that is already in the cart is refused by Shopify; tell the app that, not the stock error
            var already = (c.items || []).some(function (line) { return String(line.variant_id) === String(variant); });
            if (already) layer.send('cart-result', { ok: false, reason: 'in_cart', count: c.item_count, cart: self.brief(c) });
            else layer.send('cart-result', { ok: false, message: String((err && err.message) || err) });
          }).catch(function () {
            ticket.then(function (t) { if (t) t.pending.reject(err); });
            layer.send('cart-result', { ok: false, message: String((err && err.message) || err) });
          });
        });
    },

    remove: function (key) {
      var self = this;
      var ticket = this.announce({ action: 'remove', context: 'cart', lines: [{ id: String(key), quantity: 0 }] });
      var body = { id: String(key), quantity: 0 };
      var sections = this.sectionIds();
      if (sections.length) { body.sections = sections.join(','); body.sections_url = location.pathname; }
      var url = this.routes().cart_change_url ? this.routes().cart_change_url + '.js' : this.root() + 'cart/change.js';

      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
        .then(function (c) {
          ticket.then(function (t) {
            if (t) t.pending.resolve({ cart: t.Ev.createCartFromAjaxResponse(c), detail: { sections: c.sections, items: c.items, itemCount: c.item_count, source: CHANNEL, didError: false } });
          });
          self.redrawDrawer();
          layer.send('cart', { ok: true, cart: self.brief(c) });
        })
        .catch(function (err) {
          ticket.then(function (t) { if (t) t.pending.reject(err); });
          layer.send('cart', { ok: false, message: String((err && err.message) || err) });
        });
    },

    openDrawer: function () {
      this.redrawDrawer().then(function () {
        var panel = document.querySelector('cart-drawer-component');
        var drawer = panel && panel.closest('theme-drawer');
        if (drawer && typeof drawer.open === 'function') {
          layer.stepAside(drawer);
          drawer.open();
          return;
        }
        layer.hide({ historyHandled: true });
        location.assign(cart.routes().cart_url || '/cart');
      });
    },
  };

  /* ------------------------------------------------------------------ messages from the app */

  function leaveTo(href) {
    layer.hide({ historyHandled: true });
    location.assign(href);
  }

  var handlers = {
    'ready': function () { layer.ready(); },
    'close': function () { layer.hide(); },
    'toggle-fullscreen': function () { layer.toggleSize(); },
    'add-to-cart': function (m) { cart.add(m); },
    'cart-remove': function (m) { if (typeof m.key === 'string' && m.key) cart.remove(m.key); },
    'open-cart': function () { cart.openDrawer(); },
    'get-cart': function () {
      cart.read()
        .then(function (c) { layer.send('cart', { ok: true, cart: cart.brief(c) }); })
        .catch(function (err) { layer.send('cart', { ok: false, message: String(err) }); });
    },
    'checkout': function () { leaveTo('/checkout'); },
    'view-cart': function () { leaveTo(cart.routes().cart_url || '/cart'); },
    'navigate': function (m) {
      // the app may only send the visitor to pages of this shop
      var target;
      try { target = new URL(m.url, location.origin); } catch (_) { return; }
      if (target.origin === location.origin) leaveTo(target.href);
    },
    // the colour of the app's bottom edge, for the strip an iPhone browser may leave under the window
    'edge': function (m) {
      if (typeof m.color === 'string' && /^#[0-9a-f]{6}$/i.test(m.color)) document.documentElement.style.setProperty('--brl-edge', m.color);
    },
    'call': function (m) {
      // a frame may not start a phone call itself, so the shop page does it
      if (typeof m.number !== 'string' || !/^\+?\d{10,15}$/.test(m.number)) return;
      var link = document.createElement('a');
      link.href = 'tel:' + m.number;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  };

  addEventListener('message', function (e) {
    var m = e.data;
    if (!layer.isOpen || !layer.frame || e.source !== layer.frame.contentWindow) return;
    if (e.origin !== layer.origin || !m || m.source !== CHANNEL) return;
    var run = handlers[m.type];
    if (run) run(m);
  });

  addEventListener('keydown', function (e) { if (e.key === 'Escape' && layer.isOpen) layer.hide(); });
  addEventListener('popstate', function () { if (layer.isOpen) layer.hide({ historyHandled: true }); });

  /* ------------------------------------------------------------------ the button */

  if (!customElements.get('borr-room-launch')) {
    customElements.define('borr-room-launch', class extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        var host = this;
        var btn = this.querySelector('button');
        if (btn) btn.addEventListener('click', function () { layer.show(host); });
      }
    });
  }

  window.BorrRoomLaunch = {
    open: function (host) { layer.show(host); },
    close: function () { layer.hide(); },
  };
})();
